import { useCallback, useEffect, useMemo, useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import { NullOr } from "@regal-voice/shared-types";
import { message, Modal, Tooltip } from "antd";
import { clsx } from "clsx";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { interval, Subscription } from "rxjs";

import { RVReactRouterDirtyPrompt, RVButton, RVSideDrawer, usePageConfig } from "@regal-voice/ui";
import { usePageAvailability } from "App/routes/Hooks/usePageAvilability/usePageAvailability";
import { useCopilotHelper } from "Pages/copilot/useCopilotHelper/useCopilotHelper";
import { useFlags } from "Services/FeatureFlagService";
import { renderErrorMessage } from "Services/LoggingService";
import {
    addJourney,
    updateJourney,
    removeJourney,
    stopJourney,
    bulkUpsertJourneysStickyNotes,
} from "Services/marketing-api/journeys/mutations";
import { getJourney, getJourneyByVersion } from "Services/marketing-api/journeys/queries";
import { clearEditedNotes } from "Services/state/journeys/JourneysNotesStateSlice";
import {
    setJourney,
    updateJourney as setPartialJourney,
    reset,
    selectFlowForSave,
    setNodeAnalytics,
    selectDetails,
    selectVersion,
    selectJourneyId,
    selectMetadata,
    selectLastNodeFriendlyId,
    setJourneyUsersStats,
    selectElementDAG,
    selectTriggerNodeId,
    selectFormDirty,
    clearEditedNode,
    setIfFormDirty,
} from "Services/state/journeys/JourneyStateSlice";
import { Elements, FlowElement, Journey, JourneyStatus, Node, NodeType } from "Types/Journey";
import { allFrom } from "Vendor/dager/ElementDAG";

import { JourneysPaths } from "../paths";
import { DeleteButton } from "./Components/EditorButtons/DeleteButton/DeleteButton";
import { SaveAsDraftButton } from "./Components/EditorButtons/SaveAsDraftButton/SaveAsDraftButton";
import { SaveAsLiveButton } from "./Components/EditorButtons/SaveAsLiveButton/SaveAsLiveButton";
import { StopButton } from "./Components/EditorButtons/StopButton/StopButton";
import { JourneyBuilder } from "./Components/JourneyBuilder/JourneyBuilder";
import { useTriggerEventNode } from "./Components/JourneyBuilder/shared/hooks/useTriggerEventNode";
import { runJourneyNodesMapper } from "./Components/JourneyBuilder/shared/node-mappers";
import { JourneyCopilotHelper } from "./Components/JourneyCopilotHelper/JourneyCopilotHelper";
import { JourneyVersionsPanel } from "./Components/JourneyVersionsPanel/JourneyVersionsPanel";
import { TitleBar } from "./Components/TitleBar/TitleBar";
import {
    triggerLiveAndDateInPast,
    fetchJourneyAnalytics,
    fetchJourneyUserStats,
    isNewJourney,
    nodeIsValid,
} from "./helpers";

import styles from "./JourneyEditPage.module.scss";

const PROMPT_MESSAGE = "Leave page? You have unsaved changes.";

const DRAWER_OVERRIDES = {
    width: "100%",
};

// TODO remove when we remove allowUpdateContactNodeAndScheduledJourneys
const testForScheduledUpdateContact = (noUpdateContactNodesAllowed: boolean, element: Array<FlowElement>) => {
    if (noUpdateContactNodesAllowed) {
        return element.some((node) => node.type === NodeType.UpdateContact);
    }
    return false;
};
const DISABLED_TEXT = "Update Contact node cannot be used with Scheduled trigger.";

type CreateJourneyMutationData = {
    createJourney: Pick<Journey, "id">;
};

export function JourneyEditPage(): JSX.Element {
    const { showRegalCopilot } = usePageAvailability();

    const {
        // should be very temporary (Q3 20204)
        allowUpdateContactNodeAndScheduledJourneys,
        hasCopilotHelper,
    } = useFlags();

    const { openCopilot } = useCopilotHelper();

    const params = useParams<{ journeyId: string }>();
    const journeyId = params.journeyId as string;
    const [searchParams] = useSearchParams();
    const viewingVersion = searchParams.get("version");
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [pendingOperation, setPendingOperation] = useState<NullOr<string>>(null);
    const [loading, setLoading] = useState(true);
    const [connectedNodes, setConnectedNodes] = useState<Set<string>>();
    const [showVersionsPanel, setShowVersionsPanel] = useState(false);
    const [showExecutionMonitor, setShowExecutionMonitor] = useState(false);

    const id = useSelector(selectJourneyId);
    const triggerNodeId = useSelector(selectTriggerNodeId);
    const elements = useSelector(selectFlowForSave);
    const { name } = useSelector(selectDetails);
    const version = useSelector(selectVersion);
    const elementDAG = useSelector(selectElementDAG);
    const metadata = useSelector(selectMetadata);
    const lastNodeFriendlyId = useSelector(selectLastNodeFriendlyId);

    const { isScheduledTrigger } = useTriggerEventNode();
    const noUpdateContactNodesAllowed = allowUpdateContactNodeAndScheduledJourneys ? false : isScheduledTrigger;

    const isFormDirty = useSelector(selectFormDirty);

    usePageConfig({
        pageTitle: isNewJourney(journeyId)
            ? "New Journey"
            : viewingVersion
              ? `View Journey - Version ${viewingVersion}`
              : "Edit Journey",
    });

    useMemo(() => {
        setConnectedNodes(allFrom(elementDAG, triggerNodeId));
    }, [elementDAG, triggerNodeId]);

    useEffect(() => {
        let subscriber: Subscription | undefined;
        if (!id || isNewJourney(id) || subscriber || viewingVersion) {
            return;
        }
        subscriber = interval(15000)
            .pipe()
            .subscribe(() => {
                fetchJourneyAnalytics(id).then((analyticsData) => dispatch(setNodeAnalytics(analyticsData)));
                fetchJourneyUserStats(id).then((journeyUserCounts) =>
                    dispatch(setJourneyUsersStats(journeyUserCounts?.[0] || null))
                );
            });

        return () => {
            subscriber?.unsubscribe();
            subscriber = undefined;
        };
    }, [id, viewingVersion]);

    // Fetch specific version when viewingVersion is set
    useQuery(getJourneyByVersion, {
        variables: { id: journeyId, version: parseInt(viewingVersion || "0", 10) },
        skip: !viewingVersion || isNewJourney(journeyId),
        errorPolicy: "all",
        fetchPolicy: "cache-and-network",
        onCompleted: (data: { getJourneyByVersion: any }) => {
            const transformedJourney = runJourneyNodesMapper({
                journey: data.getJourneyByVersion,
            });
            dispatch(
                setJourney({
                    ...transformedJourney,
                    nodeAnalytics: undefined, // Don't show analytics for old versions
                })
            );
            setLoading(false);
            // Clear form dirty state when viewing versions to prevent unsaved changes modal
            dispatch(setIfFormDirty(false));
            // Clear any edited node to prevent unsaved changes modal
            dispatch(clearEditedNode());
        },
        onError: (error) =>
            renderErrorMessage({ content: `Error fetching the journey version ${error.message}.`, error }),
    });

    const { refetch: refetchActiveJourney } = useQuery(getJourney, {
        variables: { id: journeyId },
        skip: isNewJourney(journeyId) || !!viewingVersion, // Skip if viewing a specific version
        errorPolicy: "all",
        fetchPolicy: "cache-and-network",
        onCompleted: (data: { getJourney: any }) => {
            if (id == journeyId) {
                dispatch(setPartialJourney({ ...data.getJourney }));
                return;
            }
            Promise.allSettled([fetchJourneyAnalytics(journeyId), fetchJourneyUserStats(journeyId)]).then(
                ([analyticsDataResponse, journeyUserCountsResponse]: any) => {
                    const transformedJourney = runJourneyNodesMapper({
                        journey: data.getJourney,
                    });
                    dispatch(
                        setJourney({
                            ...transformedJourney,
                            nodeAnalytics: analyticsDataResponse.value,
                        })
                    );
                    dispatch(setJourneyUsersStats(journeyUserCountsResponse?.value?.[0] || null));
                    setLoading(false);
                }
            );
        },
        onError: (error) => renderErrorMessage({ content: `Error fetching the journey ${error.message}.`, error }),
    });

    const [createJourney, { loading: createPending }] = useMutation<CreateJourneyMutationData>(addJourney, {
        onError: (error) => renderErrorMessage({ content: `Creating journey failed: ${error.message}.`, error }),
    });
    const [update, { loading: updatePending }] = useMutation<Journey>(updateJourney, {
        onCompleted: () => {
            message.success("Successfully updated journey.");
            refetchActiveJourney();
            dispatch(setIfFormDirty(false));
        },
        onError: (error) => renderErrorMessage({ content: `Updating journey failed: ${error.message}.`, error }),
    });
    const [doRemove, { loading: removePending }] = useMutation<Journey>(removeJourney, {
        onCompleted: () => {
            dispatch(setIfFormDirty(false));
            // we need to give the page a chance to update
            // before we redirect so that dirty prompt is off
            setTimeout(() => {
                navigate("..", { relative: "path" });
            }, 10);
        },
        onError: (error) => renderErrorMessage({ content: `Remove journey failed: ${error.message}.`, error }),
    });
    const [stop, { loading: stopPending }] = useMutation<Journey>(stopJourney, {
        onCompleted: () => {
            message.success(
                "Successfully submitted stop request. It may take up to 5 minutes to stop all in-progress contacts."
            );
            refetchActiveJourney();
        },
        onError: (error) => {
            renderErrorMessage({ content: `Stop journey failed: ${error.message}.`, error });
            refetchActiveJourney();
        },
    });

    const [bulkUpsertStickyNotes] = useMutation(bulkUpsertJourneysStickyNotes);

    const saveNonFlowNodes = useCallback(
        (nodes: Elements<any>, savedJourneyId: string) => {
            /**
             * This is only applicable to stickyNotes right now, but if we ever implement other nodes that exist
             * independently of the flow, we can handle their save here
             */

            /*
            For Sticky Notes, the only thing we care to save here is their latest posisiton, which
             may have changed since the note was last submitted
             **/
            const stickyNotes = nodes
                .filter((node) => node.type === NodeType.StickyNote)
                .map((node) => ({
                    id: node.id,
                    position: (node as Node).position,
                    data: node.data,
                }));

            if (!stickyNotes.length) {
                return undefined;
            }

            // Save notes to db
            return bulkUpsertStickyNotes({
                variables: {
                    journeyId: savedJourneyId,
                    stickyNotesInput: { stickyNotes },
                },
                onError: () => {
                    message.warning("Error saving latest sticky note positions");
                },
            });
        },
        [bulkUpsertStickyNotes]
    );

    useEffect(() => {
        if (isNewJourney(journeyId) && id != "duplicate") {
            dispatch(reset());
            setLoading(false);
        }
    }, [id, journeyId, dispatch]);

    const incrementedVersion = useMemo(() => {
        return parseInt((version as any) || 0) + 1;
    }, [version]);

    const handleSave = useCallback(
        async (state: string, overrideStateData?: Record<string, any>) => {
            // Stickies are not translated, and exist in their own table
            const nonFlowNodes = elements.filter((el) => el.type === NodeType.StickyNote);
            const flowNodes = elements.filter((el) => el.type !== NodeType.StickyNote);
            const data: {
                id?: string;
                name: string;
                state: string;
                flow: Elements<any>;
                version: number;
                usedNodeCount: number;
                description?: string;
            } = {
                id: journeyId != "new" ? journeyId : undefined,
                name,
                state,
                flow: flowNodes,
                // version: incrementVersion(),
                version: incrementedVersion,
                usedNodeCount: lastNodeFriendlyId,
                ...(!!overrideStateData ? overrideStateData : {}),
            };

            if (metadata.usedNodeCount !== undefined) {
                data.usedNodeCount = Math.max(data.usedNodeCount, metadata.usedNodeCount);
            }
            if (triggerLiveAndDateInPast(state, data)) {
                const error = new RangeError("scheduled time in trigger node cannot be in the past");
                renderErrorMessage({
                    content: `Failed to save Journey as live: scheduled time in trigger node cannot be in the past.`,
                    error,
                });
            } else {
                setPendingOperation(state);
                const isCreatingJourney = isNewJourney(journeyId);
                dispatch(clearEditedNode());
                dispatch(clearEditedNotes({ journeyId }));
                setPendingOperation(null);
                if (isCreatingJourney) {
                    const saveResult = await createJourney({ variables: data });
                    const savedJourneyId = saveResult.data?.createJourney.id;
                    if (!savedJourneyId) {
                        return;
                    }
                    await saveNonFlowNodes(nonFlowNodes, savedJourneyId);
                    message.success("Successfully created journey.");
                    dispatch(setIfFormDirty(false));
                    // we need to give the page a chance to update
                    // before we redirect so that dirty prompt is off
                    setTimeout(() => {
                        navigate(`../${savedJourneyId}`, { relative: "path", replace: true });
                    }, 10);
                } else {
                    await update({ variables: data });
                    await saveNonFlowNodes(nonFlowNodes, journeyId);
                }
            }
        },
        [
            elements,
            journeyId,
            name,
            incrementedVersion,
            lastNodeFriendlyId,
            metadata.usedNodeCount,
            createJourney,
            update,
            dispatch,
            saveNonFlowNodes,
        ]
    );

    async function handleRemove() {
        await doRemove({ variables: { id: journeyId } });
    }

    async function handleStop() {
        await stop({ variables: { id: journeyId, version: incrementedVersion } });
    }

    async function handleClone() {
        const data = {
            id: "duplicate",
            name: `${name} - (COPY)`,
            state: JourneyStatus.DRAFT,
            flow: elements,
            createdAt: "",
            originalCreatedAt: "",
            updatedAt: "",
            version: 0,
            metadata,
        } as unknown as Journey;
        dispatch(setJourney(data));
        navigate(`..${JourneysPaths.new}`, { relative: "path" });
    }

    function showConfirmationModal() {
        Modal.confirm({
            title: "Duplicate Journey",
            content:
                "Duplicating this journey will clone the current journey and overwrite the page. Cancel and save the current journey before duplicating, if you want to save the current progress.",
            okText: "Yes, Duplicate Journey",
            cancelText: "Cancel",
            onOk: handleClone,
        });
    }

    const disabledForUpdate = useMemo(() => {
        return testForScheduledUpdateContact(noUpdateContactNodesAllowed, elements);
    }, [noUpdateContactNodesAllowed, elements]);

    const isLiveSaveDisabled = useMemo(() => {
        // journey can be live only if trigger node has eventName and triggerType defined
        if (!triggerNodeId) {
            return true;
        }
        // TODO: remove when we remove allowUpdateContactNodeAndScheduledJourneys
        return !elements.filter((element) => connectedNodes?.has(element.id)).every(nodeIsValid) || disabledForUpdate;
    }, [triggerNodeId, elements, disabledForUpdate, connectedNodes]);

    const isEditDisabled = createPending || updatePending || removePending || !!viewingVersion || showVersionsPanel;

    useEffect(() => {
        return () => {
            dispatch(reset());
            dispatch(clearEditedNode());
        };
    }, [dispatch]);

    const handleToggleVersionsPanel = useCallback(() => {
        setShowVersionsPanel((prev) => !prev);
        // Clear form dirty state when toggling versions panel to prevent unsaved changes modal
        dispatch(setIfFormDirty(false));
        // Clear any edited node to ensure clean state
        dispatch(clearEditedNode());
        refetchActiveJourney();
    }, [refetchActiveJourney, dispatch]);

    const openExecutionMonitor = useCallback(() => setShowExecutionMonitor(true), []);
    const closeExecutionMonitor = useCallback(() => setShowExecutionMonitor(false), []);

    const handleCopilotNavigate = useCallback(
        (prompt: string) => {
            if (!isNewJourney(journeyId)) {
                openCopilot(`${prompt} For journey ID: ${journeyId}`);
            }
        },
        [openCopilot, journeyId]
    );

    return (
        <div className={styles.journeyEditorContainer}>
            {/* remove hasCopilotHelper when we move the widget so it isnt covering the journey save button,
             this was a quick patch to get around qa wolf tests */}
            {showRegalCopilot && hasCopilotHelper && !isNewJourney(journeyId) && (
                <JourneyCopilotHelper onNavigate={handleCopilotNavigate} />
            )}
            <TitleBar
                journeyId={journeyId}
                loading={loading}
                onVersionClick={handleToggleVersionsPanel}
                showVersionsPanel={showVersionsPanel}
                onSearchExecutions={openExecutionMonitor}
            />
            <div className={clsx(styles.contentContainer, { [styles.globalSidePanelOpen]: showVersionsPanel })}>
                <div className={styles.canvasContainer}>
                    <JourneyBuilder
                        loading={loading}
                        saveJourney={handleSave}
                        saving={!!pendingOperation}
                        isViewOnly={!!viewingVersion || showVersionsPanel}
                        showExecutionMonitor={showExecutionMonitor}
                        onOpenExecutionMonitor={openExecutionMonitor}
                        onCloseExecutionMonitor={closeExecutionMonitor}
                    />
                </div>
            </div>
            <RVSideDrawer
                className={styles.globalSidePanel}
                open={showVersionsPanel}
                onClose={handleToggleVersionsPanel}
                hideHeader
                overrides={DRAWER_OVERRIDES}
            >
                <JourneyVersionsPanel onClose={handleToggleVersionsPanel} />
            </RVSideDrawer>
            <div className={clsx(styles.actionBar, "flex-row")}>
                <div className={clsx(styles.actionButtons, "flex-row")}>
                    <DeleteButton
                        disabled={isEditDisabled || isNewJourney(journeyId)}
                        handleRemove={handleRemove}
                        removePending={removePending}
                    />
                    <StopButton
                        disabled={isEditDisabled || isNewJourney(journeyId)}
                        handleStop={handleStop}
                        stopPending={stopPending}
                    />
                </div>
                <div className={clsx(styles.actionButtons, "flex-row")}>
                    <RVButton
                        size="middle"
                        disabled={isEditDisabled || isNewJourney(journeyId)}
                        onClick={showConfirmationModal}
                        data-testid="duplicate-button"
                    >
                        Duplicate
                    </RVButton>
                    <SaveAsDraftButton
                        disabled={isEditDisabled}
                        handleSaveDraft={() => handleSave(JourneyStatus.DRAFT)}
                        pendingOperation={pendingOperation}
                    />
                    <Tooltip title={disabledForUpdate ? DISABLED_TEXT : ""} placement={"topRight"}>
                        {/* we need to have a span here so that the tool tip
                            has something to attach too.
                            It isnt able to attach to the disabled button
                            topRight helps with the placement
                        */}
                        <span></span>
                        <SaveAsLiveButton
                            disabled={isEditDisabled || isLiveSaveDisabled}
                            handleSaveLive={() => handleSave(JourneyStatus.LIVE)}
                            pendingOperation={pendingOperation}
                        />
                    </Tooltip>
                </div>
            </div>

            <RVReactRouterDirtyPrompt
                whenDirty={isFormDirty && !viewingVersion && !showVersionsPanel}
                message={PROMPT_MESSAGE}
            />
        </div>
    );
}
